import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { exportPdf, exportExcel } from "@/lib/exports";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid,
} from "recharts";
import {
  ListChecks, Clock, CheckCircle2, TrendingUp, Loader2, Layers, Filter,
  MessageCircle, DoorOpen, FileDown, FileSpreadsheet, Calendar, Plus, Trash2,
  Mail, Send, ShieldCheck,
} from "lucide-react";

const COLORS = ["#4e73df", "#1cc88a", "#f6c23e", "#e74a3b", "#845adf", "#36b9cc"];
const STATUSES = ["PENDING", "IN PROGRESS", "CLOSED"];

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl gm-shadow p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}1a`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#3a3b45] leading-none">{value}</p>
        <p className="text-xs text-slate-400 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function Reports() {
  const { user, settings } = useAuth();
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", department: "", status: "" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/stats/overview", { params }).then(({ data }) => setStats(data));
  }, [f]);

  useEffect(() => { api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const fld = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]";

  return (
    <div className="gm-fade-up space-y-6">
      <h2 className="font-bold text-lg text-[#3a3b45]">Analytics & Reports</h2>

      <div className="bg-white rounded-2xl gm-shadow p-5">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 mb-3"><Filter className="w-4 h-4 text-[#4e73df]" /> Filters</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">From</label><input type="date" className={fld} data-testid="report-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-500">To</label><input type="date" className={fld} data-testid="report-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-500">Department</label>
            <select className={fld} data-testid="report-dept" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })}><option value="">All</option>{departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-slate-500">Status</label>
            <select className={fld} data-testid="report-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="">All</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div className="flex items-end"><button onClick={() => setF({ date_from: "", date_to: "", department: "", status: "" })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Reset</button></div>
        </div>
      </div>

      {!stats ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4e73df]" /></div>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ListChecks} label="Total Glitches" value={stats.total} color="#4e73df" />
        <Stat icon={Clock} label="Pending" value={stats.pending} color="#f6c23e" />
        <Stat icon={CheckCircle2} label="Closed" value={stats.closed} color="#1cc88a" />
        <Stat icon={TrendingUp} label="Created Today" value={stats.today} color="#845adf" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#4e73df]" /> Last 7 Days Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#858796" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#858796" }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#4e73df" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4 flex items-center gap-2"><Layers className="w-4 h-4 text-[#4e73df]" /> By Department</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.by_department}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#858796" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#858796" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#4e73df" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4">Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={stats.by_status} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {stats.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4">Top Forbes Classifications</h3>
          {stats.by_class.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">No classification data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.by_class} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#858796" }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#858796" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1cc88a" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      </>
      )}

      <GuestLogAnalytics />
      <HostEngagement />
      <DailyDigest appName={settings?.general?.app_name} />
      {user?.role === "admin" && <ScheduledReports />}
    </div>
  );
}

/* ==================================================================== */
/* GUEST LOG ANALYTICS                                                  */
/* ==================================================================== */
function GuestLogAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/stats/guest-log").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="space-y-4">
      <h2 className="font-bold text-lg text-[#3a3b45] flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#4e73df]" /> Guest Log Analytics</h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={MessageCircle} label="Total Threads" value={d.total_threads} color="#4e73df" />
        <Stat icon={Clock} label="Open" value={d.open_threads} color="#f6c23e" />
        <Stat icon={CheckCircle2} label="Closed" value={d.closed_threads} color="#1cc88a" />
        <Stat icon={Layers} label="Total Notes" value={d.total_entries} color="#845adf" />
        <Stat icon={TrendingUp} label="Avg Notes / Thread" value={d.avg_entries_per_thread} color="#36b9cc" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4">Busiest Rooms (by notes)</h3>
          {d.busiest_rooms.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No activity yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.busiest_rooms} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#858796" }} />
                <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 11, fill: "#858796" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#4e73df" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl gm-shadow p-5">
          <h3 className="font-semibold text-[#3a3b45] mb-4">Most Active Hosts</h3>
          {d.busiest_hosts.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No activity yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.busiest_hosts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#858796" }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: "#858796" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1cc88a" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* HOST ENGAGEMENT — who is talking to guests, who isn't                */
/* ==================================================================== */
function HostEngagement() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/reports/engagement").then(({ data }) => setD(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const columns = [
    { header: "Room", key: "room_no" }, { header: "Guest", key: "guest_name" },
    { header: "Check-Out", key: "checkout" }, { header: "Guest Log Started?", key: "has_log_label" },
    { header: "Notes", key: "entry_count" },
  ];
  const rowsForExport = (d?.rooms || []).map((r) => ({ ...r, has_log_label: r.has_log ? "Yes" : "No" }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-bold text-lg text-[#3a3b45] flex items-center gap-2"><DoorOpen className="w-5 h-5 text-[#4e73df]" /> Guest Engagement — Who's Talking to Guests</h2>
        {d && d.rooms.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => exportPdf("guest-engagement", "Guest Engagement Report", columns, rowsForExport)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50"><FileDown className="w-3.5 h-3.5" /> PDF</button>
            <button onClick={() => exportExcel("guest-engagement", "Engagement", columns, rowsForExport)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>
      ) : !d || d.rooms.length === 0 ? (
        <div className="bg-white rounded-2xl gm-shadow py-14 text-center text-slate-400"><p className="text-sm">No in-house guests found — upload an Opera export to populate this.</p></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Stat icon={DoorOpen} label="In-House Rooms" value={d.total_in_house} color="#4e73df" />
            <Stat icon={CheckCircle2} label="Engaged" value={d.engaged} color="#1cc88a" />
            <Stat icon={TrendingUp} label="Engagement Rate" value={`${d.engagement_pct}%`} color={d.engagement_pct >= 60 ? "#1cc88a" : d.engagement_pct >= 30 ? "#f6c23e" : "#e74a3b"} />
          </div>
          <div className="bg-white rounded-2xl gm-shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                  <th className="px-4 py-3">Room</th><th className="px-4 py-3">Guest</th><th className="px-4 py-3">Check-Out</th>
                  <th className="px-4 py-3">Guest Log</th><th className="px-4 py-3">Notes</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {d.rooms.map((r) => (
                    <tr key={r.room_no + r.checkout} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-semibold text-[#4e73df]">{r.room_no}</td>
                      <td className="px-4 py-3 text-slate-600">{r.guest_name || "-"}</td>
                      <td className="px-4 py-3 text-slate-500">{r.checkout || "-"}</td>
                      <td className="px-4 py-3">
                        {r.has_log ? (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Started</span>
                        ) : (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Not started</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{r.entry_count || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ==================================================================== */
/* END-OF-DAY DIGEST — with PDF / Excel export                         */
/* ==================================================================== */
function DailyDigest({ appName }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/daily", { params: { date } }).then(({ data }) => setD(data)).catch(() => {}).finally(() => setLoading(false));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const glitchCols = [
    { header: "Serial", key: "serial" }, { header: "Room", key: "room_no" }, { header: "Location", key: "location" },
    { header: "Priority", key: "priority" }, { header: "Status", key: "status" }, { header: "Description", key: "description" },
  ];
  const noteCols = [
    { header: "Room", key: "room_no" }, { header: "Guest", key: "guest_name" }, { header: "Note", key: "note" }, { header: "By", key: "logged_by" },
  ];

  const doExportPdf = () => {
    if (!d) return;
    exportPdf(`daily-digest-${date}`, `${appName || "Glitch Manager"} — Daily Digest`, glitchCols, d.glitches_created, `${date} · Guest Log notes: ${d.guest_log_entries.length}`);
  };
  const doExportExcel = () => {
    if (!d) return;
    exportExcel(`daily-digest-${date}`, "Glitches", glitchCols, d.glitches_created);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-bold text-lg text-[#3a3b45] flex items-center gap-2"><Calendar className="w-5 h-5 text-[#4e73df]" /> End-of-Day Report</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]" />
          <button onClick={doExportPdf} disabled={!d} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-50"><FileDown className="w-3.5 h-3.5" /> PDF</button>
          <button onClick={doExportExcel} disabled={!d} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-50"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
        </div>
      </div>
      {loading || !d ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={ListChecks} label="Glitches Created" value={d.glitches_created.length} color="#4e73df" />
            <Stat icon={CheckCircle2} label="Glitches Closed" value={d.glitches_closed.length} color="#1cc88a" />
            <Stat icon={MessageCircle} label="Guest Log Notes" value={d.guest_log_entries.length} color="#845adf" />
          </div>
          <div className="bg-white rounded-2xl gm-shadow overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 font-semibold text-sm text-slate-600">Glitches Created</div>
            {d.glitches_created.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">None.</p> : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                  <th className="px-4 py-2">#</th><th className="px-4 py-2">Room</th><th className="px-4 py-2">Location</th><th className="px-4 py-2">Priority</th><th className="px-4 py-2">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {d.glitches_created.map((g) => (
                    <tr key={g.id}><td className="px-4 py-2 font-semibold text-[#4e73df]">{g.serial}</td><td className="px-4 py-2">{g.room_no || "-"}</td><td className="px-4 py-2">{g.location}</td><td className="px-4 py-2">{g.priority}</td><td className="px-4 py-2">{g.status}</td></tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ==================================================================== */
/* SCHEDULED REPORTS — admin can set up recurring emailed digests       */
/* ==================================================================== */
function ScheduledReports() {
  const { settings } = useAuth();
  const appTz = settings?.general?.timezone || "Asia/Dhaka";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", frequency: "daily", hour: 8, day_of_week: 1, recipients: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/report-schedules").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.recipients.trim()) { toast.error("Name and recipients are required"); return; }
    setSaving(true);
    try {
      await api.post("/report-schedules", form);
      toast.success("Schedule created");
      setForm({ name: "", frequency: "daily", hour: 8, day_of_week: 1, recipients: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (r) => { try { await api.put(`/report-schedules/${r.id}`, { enabled: !r.enabled }); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const del = async (r) => { if (!window.confirm(`Delete schedule "${r.name}"?`)) return; try { await api.delete(`/report-schedules/${r.id}`); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const runNow = async (r) => { try { const { data } = await api.post(`/report-schedules/${r.id}/run-now`); toast[data.result?.ok ? "success" : "error"](data.result?.ok ? "Sent" : (data.result?.error || "SMTP not configured")); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };

  const fld = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-[#4e73df]";
  const DAYS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="space-y-4">
      <h2 className="font-bold text-lg text-[#3a3b45] flex items-center gap-2"><Mail className="w-5 h-5 text-[#4e73df]" /> Scheduled Report Emails <span className="text-xs font-normal text-slate-400">(admin)</span></h2>
      <div className="bg-white rounded-2xl gm-shadow p-5">
        <p className="text-xs text-slate-500 mb-3">Sends the day-so-far digest automatically once the scheduled time has passed (checked whenever anyone is using the app). Uses the SMTP settings configured in Admin → Settings → Email.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Name</label><input className={fld} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Morning Digest" /></div>
          <div><label className="text-xs font-semibold text-slate-500">Frequency</label>
            <select className={fld} value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}><option value="daily">Daily</option><option value="weekly">Weekly</option></select></div>
          {form.frequency === "weekly" && (
            <div><label className="text-xs font-semibold text-slate-500">Day</label>
              <select className={fld} value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: Number(e.target.value) })}>{DAYS.slice(1).map((d, i) => <option key={d} value={i + 1}>{d}</option>)}</select></div>
          )}
          <div><label className="text-xs font-semibold text-slate-500">Hour ({appTz})</label>
            <select className={fld} value={form.hour} onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}>{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select></div>
          <div className={form.frequency === "weekly" ? "lg:col-span-1" : "sm:col-span-2 lg:col-span-2"}>
            <label className="text-xs font-semibold text-slate-500">Recipients (comma-separated)</label>
            <input className={fld} value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="gm@hotel.com, ops@hotel.com" />
          </div>
        </div>
        <button onClick={create} disabled={saving} className="mt-3 flex items-center gap-2 text-sm font-semibold text-white bg-[#4e73df] hover:bg-[#3a5fd0] rounded-lg px-4 py-2.5 disabled:opacity-60"><Plus className="w-4 h-4" /> Add Schedule</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl gm-shadow py-10 text-center text-slate-400 text-sm">No scheduled reports yet.</div>
      ) : (
        <div className="bg-white rounded-2xl gm-shadow divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-sm text-[#3a3b45]">{r.name} <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.enabled ? "Active" : "Paused"}</span></p>
                <p className="text-xs text-slate-400 mt-1">{r.frequency === "weekly" ? `Weekly · ${DAYS[r.day_of_week]}` : "Daily"} · {String(r.hour).padStart(2, "0")}:00 · {r.recipients}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => runNow(r)} title="Send now" className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><Send className="w-4 h-4" /></button>
                <button onClick={() => toggle(r)} title={r.enabled ? "Pause" : "Resume"} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><ShieldCheck className="w-4 h-4" /></button>
                <button onClick={() => del(r)} title="Delete" className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
